import React, { useEffect, useRef, useState } from "react";

/**
 * Reveals its children once they scroll into view.
 *
 * Uses a single IntersectionObserver per element and unobserves after the
 * first reveal, so nothing keeps running once an element has appeared.
 * The transition itself is CSS (see `.reveal` in App.css) and animates
 * only opacity + transform, which stay on the compositor.
 *
 * Respects prefers-reduced-motion: reduced-motion users get the content
 * immediately with no transform.
 */
const Reveal = ({
  children,
  as: Tag = "div",
  delay = 0,
  className = "",
  ...rest
}) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    // No IntersectionObserver, or the user prefers reduced motion:
    // show immediately rather than risk content never appearing.
    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (prefersReduced || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return undefined;
    }

    // threshold 0 — fire as soon as any part of the element enters the
    // root. A fractional threshold is unsafe here: an element taller than
    // the viewport can never reach it (the visible fraction caps at
    // viewportHeight / elementHeight), so tall sections would stay at
    // opacity 0 forever. The negative bottom margin is what delays the
    // reveal until the element is properly on screen.
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          setVisible(true);
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0, rootMargin: "0px 0px -10% 0px" }
    );

    /* Anything already on screen at mount is shown immediately, with no
       observer round-trip — above-the-fold content should never wait a
       frame to appear. */
    const box = node.getBoundingClientRect();
    if (box.top < window.innerHeight && box.bottom > 0) {
      setVisible(true);
      return undefined;
    }

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <Tag
      ref={ref}
      className={`reveal ${visible ? "is-visible" : ""} ${className}`.trim()}
      style={delay ? { "--reveal-delay": `${delay}ms` } : undefined}
      {...rest}
    >
      {children}
    </Tag>
  );
};

export default Reveal;
